import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card.jsx';
import { Mail, MessageSquareMore, Calendar, Info, ChevronDown, PhoneForwarded, PhoneIncoming, SendHorizonal } from "lucide-react";
import { DateTime } from "luxon";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible.jsx";
import AudioPlayer from "./AudioPlayer.jsx";
import RenderEmailContent from "./RenderEmailContent.jsx";
import ShadcnStyleLogsPdf from "./ShadcnStyleLogsPdf.jsx";
import { Button } from '@/components/ui/button.jsx';
import { Skeleton } from '@/components/ui/skeleton.jsx';


const LogsCard = ({ logs = [], secretShopper = {}, community = {}, isLoading }) => {
    const getHeader = (log) => {
        if (log.loggable_type === 'SecretShopperPhoneLog') {
            const isOutboundPhoneCall = log.details.from === secretShopper.phone_number;

            return isOutboundPhoneCall ? <><PhoneForwarded className="h-5 w-5 text-gray-500 mr-2" />Secret Shopper To Community </> : <><PhoneIncoming className="h-5 w-5 text-gray-500 mr-2" />{log.details.from} To Secret Shopper</>
        }

        if (log.loggable_type === 'SecretShopperSmsLog') {
            return <><MessageSquareMore className="h-5 w-5 text-gray-500 mr-2" />Text From {log.details.from}</>
        }

        if (log.loggable_type === 'SecretShopperEmailLog') {
            return <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center">
                    <SendHorizonal className="h-5 w-5 text-gray-500 mr-2" />{log.details.sender_email}
                </div>
                <div className="flex flex-row items-center">
                    <Mail className="h-5 w-5 text-gray-500 mr-2" />{log.details.subject}
                </div>
            </div>
        }

        if (log.loggable_type === 'SecretShopperMessageLog') {
            return <><Info className="h-5 w-5 text-gray-500 mr-2" />{log.details.message}</>
        }

        return 'No Message Available';
    }

    const formatDate = (dateString) => {
        return DateTime.fromISO(dateString)
            .setZone(community.time_zone)
            .toFormat('MMMM dd, yyyy hh:mm a');
    };

    const getLogBody = (log) => {
        if (log.loggable_type === 'SecretShopperPhoneLog') {
            if (!log?.details.voicemail_recording_signed_url) {
                return 'No Recording Available';
            }

            return <AudioPlayer audioUrl={log.details.voicemail_recording_signed_url} />;
        }

        if (log.loggable_type === 'SecretShopperSmsLog') {
            return log.details.body;
        }

        if (log.loggable_type === 'SecretShopperEmailLog') {
            return <RenderEmailContent html={log.details.body} {...log.details} />
        }

        if (log.loggable_type === 'SecretShopperMessageLog') {
            return <Info className="h-5 w-5 text-gray-500" />
        }

        return 'No additional details available.';
    }

    if (isLoading) {
        return (
            <Card className="w-full max-w-7xl mx-auto shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-semibold flex justify-between">
                        <span>Secret Shopper Activity Log</span>
                        <Button variant="outline" disabled>Download</Button>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-2">
                                <div className="flex flex-row justify-between">
                                    <div className="flex flex-col space-y-2">
                                        <Skeleton className="w-64 h-5" />
                                        <Skeleton className="w-40 h-4" />
                                    </div>
                                    <Skeleton className="h-6 w-6" />
                                </div>
                                <Skeleton className="w-full h-10" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full max-w-7xl mx-auto shadow-lg">
            <CardHeader>
                <CardTitle className="text-xl font-semibold flex justify-between">
                    <span>Secret Shopper Activity Log</span>
                    <ShadcnStyleLogsPdf
                        timezone={community.time_zone}
                        secretShopper={secretShopper}
                        community={community}
                        logs={logs}
                    />
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {logs?.map((log) => (
                        <Collapsible key={log.id} className="bg-gray-50 p-4 rounded-lg space-y-2">
                            <div className="flex flex-row justify-between">
                                <div className="flex flex-col">
                                    <div className="flex items-center space-x-2 mb-2">
                                        {getHeader(log)}
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                        <Calendar className="h-4 w-4" />
                                        <span>{formatDate(log.created_at)}</span>
                                    </div>
                                </div>
                                <CollapsibleTrigger className="flex items-center text-black space-x-1 cursor-pointer">
                                    <ChevronDown className="h-6 w-6" />
                                </CollapsibleTrigger>
                            </div>
                            <CollapsibleContent className="pl-6 pt-2">
                                <div className="text-sm text-gray-700">
                                    {getLogBody(log)}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export default LogsCard;